export interface RecordingOptions {
  mimeType?: string
  videoBitsPerSecond?: number
  audioBitsPerSecond?: number
  includeAudio?: boolean
  includeMicrophone?: boolean
  arVideoElement?: HTMLVideoElement // optional AR video element to capture audio from
}

export interface RecordingResult {
  success: boolean
  blob?: Blob
  url?: string
  error?: string
  type: 'video' | 'screenshot'
  format?: string
}

/**
 * Simple, cross-platform VideoRecorder
 * - Composites camera feed + provided AR canvas into a single canvas
 * - Captures canvas stream via captureStream()
 * - Optionally mixes microphone and/or AR video audio
 * - Uses MediaRecorder to record and auto-download the resulting file
 */
export class VideoRecorder {
  private mediaRecorder: MediaRecorder | null = null
  private recordedChunks: Blob[] = []
  private cameraStream: MediaStream | null = null
  private canvasStream: MediaStream | null = null
  private compositeStream: MediaStream | null = null
  private compositeCanvas: HTMLCanvasElement | null = null
  private compositeContext: CanvasRenderingContext2D | null = null
  private arCanvas: HTMLCanvasElement | null = null
  private videoElement: HTMLVideoElement | null = null
  private isRecording = false
  private recordingStartTime = 0
  private rafId: number | null = null
  private audioContext: AudioContext | null = null
  private audioDestination: MediaStreamAudioDestinationNode | null = null

  isSupported(): boolean {
    return typeof MediaRecorder !== 'undefined'
  }

  private isIPhone(): boolean {
    return /iPhone/.test(navigator.userAgent)
  }

  private getBestMimeType(preferred?: string): string {
    if (preferred && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(preferred)) {
      return preferred
    }
    const candidates = this.isIPhone()
      ? ['video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
      : ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
    for (const t of candidates) {
      try {
        if ((MediaRecorder as any).isTypeSupported && (MediaRecorder as any).isTypeSupported(t)) return t
      } catch (e) {
        // ignore
      }
    }
    return 'video/webm'
  }

  async startRecording(arCanvas: HTMLCanvasElement, options: RecordingOptions = {}): Promise<boolean> {
    if (this.isRecording) return false
    this.arCanvas = arCanvas
    this.recordedChunks = []

    try {
      // create composite canvas sized to AR canvas
      this.compositeCanvas = document.createElement('canvas')
      this.compositeCanvas.width = arCanvas.width || window.innerWidth
      this.compositeCanvas.height = arCanvas.height || window.innerHeight
      this.compositeContext = this.compositeCanvas.getContext('2d', { alpha: false })
      if (!this.compositeContext) throw new Error('Failed to get canvas context')

      // Try to get camera (back-facing when available). If camera is already
      // in use by the AR engine, fallback to reusing an existing <video>
      // element that already has the camera stream attached.
      try {
        this.cameraStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: options.includeMicrophone === true
        })

        // hidden video element for camera playback
        this.videoElement = document.createElement('video')
        this.videoElement.autoplay = true
        this.videoElement.playsInline = true
        this.videoElement.muted = true
        this.videoElement.style.display = 'none'
        this.videoElement.srcObject = this.cameraStream
        document.body.appendChild(this.videoElement)
        try { await this.videoElement.play() } catch (e) { /* ignore */ }
        console.log('VideoRecorder: Obtained camera stream via getUserMedia')
      } catch (getErr) {
        console.warn('VideoRecorder: getUserMedia failed, attempting to reuse existing video element:', getErr)

        // Find an existing video element that already has a camera stream
        const videos = Array.from(document.querySelectorAll('video')) as HTMLVideoElement[]
        const cameraCandidate = videos.find(v => {
          try {
            const so = (v as any).srcObject as MediaStream | null
            return so && so.getVideoTracks && so.getVideoTracks().length > 0
          } catch (e) { return false }
        })

        if (cameraCandidate) {
          this.videoElement = cameraCandidate
          // Try to reuse the existing stream object for audio if requested
          try {
            this.cameraStream = (cameraCandidate as any).srcObject as MediaStream
            console.log('VideoRecorder: Reusing existing camera video element for recording')
          } catch (e) {
            console.warn('VideoRecorder: Failed to read srcObject from existing video element', e)
          }
        } else {
          // No available camera to reuse
          throw getErr
        }
      }

      // draw loop: camera first, AR canvas on top
      const drawFrame = () => {
        if (!this.compositeContext || !this.compositeCanvas || !this.videoElement) return
        // match sizes
        const w = this.compositeCanvas.width
        const h = this.compositeCanvas.height
        this.compositeContext.clearRect(0, 0, w, h)
        try { this.compositeContext.drawImage(this.videoElement, 0, 0, w, h) } catch (e) { /* ignore */ }
        try { this.compositeContext.drawImage(this.arCanvas!, 0, 0, w, h) } catch (e) { /* ignore */ }
        this.rafId = requestAnimationFrame(drawFrame)
      }

      this.isRecording = true
      drawFrame()

      // wait a short moment for frames to render
      await new Promise(resolve => setTimeout(resolve, 200))

      this.canvasStream = this.compositeCanvas.captureStream(30)
      if (!this.canvasStream) {
        throw new Error('captureStream() returned null')
      }
      if (this.canvasStream.getVideoTracks().length === 0) {
        console.warn('VideoRecorder: captureStream produced no video tracks')
        // still continue and let MediaRecorder or downstream logic fail with a clearer message
      } else {
        console.log('VideoRecorder: Canvas stream video tracks:', this.canvasStream.getVideoTracks().length)
      }

      // prepare composite stream: video from canvas + audio (microphone and optional ar video)
      this.compositeStream = new MediaStream()
      if (this.canvasStream) {
        for (const t of this.canvasStream.getVideoTracks()) this.compositeStream.addTrack(t)
      }

      // audio mixing: create AudioContext destination and add tracks
      if (options.includeAudio) {
        this.audioContext = new AudioContext()
        this.audioDestination = this.audioContext.createMediaStreamDestination()

        // microphone (if present)
        if (this.cameraStream && this.cameraStream.getAudioTracks().length > 0) {
          try {
            const micSrc = this.audioContext.createMediaStreamSource(this.cameraStream)
            micSrc.connect(this.audioDestination)
          } catch (e) { /* ignore */ }
        }

        // AR video element audio via captureStream (preferred)
        if (options.arVideoElement && (options.arVideoElement as any).captureStream) {
          try {
            const arStream = (options.arVideoElement as any).captureStream?.(30)
            if (arStream && arStream.getAudioTracks().length > 0) {
              const arSrc = this.audioContext.createMediaStreamSource(arStream)
              arSrc.connect(this.audioDestination)
            }
          } catch (e) { /* ignore */ }
        }

        // attach audio tracks to compositeStream
        if (this.audioDestination) {
          for (const t of this.audioDestination.stream.getAudioTracks()) this.compositeStream.addTrack(t)
        }
      }

      // prepare MediaRecorder
      const mimeType = this.getBestMimeType(options.mimeType)
      const recorderOptions: MediaRecorderOptions = {
        mimeType,
        videoBitsPerSecond: options.videoBitsPerSecond || 2500000,
        audioBitsPerSecond: options.audioBitsPerSecond || 128000
      }

      this.mediaRecorder = new MediaRecorder(this.compositeStream, recorderOptions)
      this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) this.recordedChunks.push(e.data)
      }

      this.mediaRecorder.onerror = (ev) => console.error('MediaRecorder error', ev)

      this.mediaRecorder.start(500)
      this.recordingStartTime = Date.now()
      return true
    } catch (error: any) {
      console.error('startRecording failed', error)
      this.cleanup()
      return false
    }
  }

  async stopRecording(): Promise<RecordingResult> {
    if (!this.isRecording) return { success: false, error: 'No recording in progress', type: 'video' }

    return new Promise(resolve => {
      if (!this.mediaRecorder) {
        this.cleanup()
        resolve({ success: false, error: 'Recorder not initialized', type: 'video' })
        return
      }

      const finalize = () => {
        try {
          const mime = this.mediaRecorder?.mimeType || 'video/webm'
          const blob = new Blob(this.recordedChunks, { type: mime })
          const url = URL.createObjectURL(blob)

          // auto-download
          try {
            const ext = this.isIPhone() ? 'mp4' : mime.includes('mp4') ? 'mp4' : mime.includes('quicktime') ? 'mov' : 'webm'
            const a = document.createElement('a')
            a.style.display = 'none'
            a.href = url
            a.download = `rockar-recording-${Date.now()}.${ext}`
            document.body.appendChild(a)
            a.click()
            setTimeout(() => { a.remove() }, 1000)
          } catch (e) { /* ignore download failure */ }

          const format = this.isIPhone() ? 'mp4' : mime.includes('mp4') ? 'mp4' : 'webm'
          resolve({ success: true, blob, url, type: 'video', format })
        } catch (e: any) {
          resolve({ success: false, error: e?.message || 'Unknown', type: 'video' })
        } finally {
          this.cleanup()
        }
      }

      this.mediaRecorder.onstop = () => finalize()

      try {
        if (this.mediaRecorder.state === 'recording') this.mediaRecorder.stop()
        else finalize()
      } catch (e) {
        console.warn('Error stopping MediaRecorder', e)
        finalize()
      }
    })
  }

  takeScreenshot(canvas: HTMLCanvasElement): RecordingResult {
    try {
      const url = canvas.toDataURL('image/png')
      return { success: true, url, type: 'screenshot' }
    } catch (e: any) {
      return { success: false, error: e?.message || 'Screenshot failed', type: 'screenshot' }
    }
  }

  getIsRecording(): boolean { return this.isRecording }
  getRecordingDuration(): number { return this.isRecording ? Date.now() - this.recordingStartTime : 0 }

  destroy(): void { if (this.isRecording) this.stopRecording().catch(()=>{}) }

  private cleanup(): void {
    this.isRecording = false
    this.recordingStartTime = 0
    if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try { this.mediaRecorder.stop() } catch (e) {}
    }
    this.mediaRecorder = null
    this.recordedChunks = []

    if (this.videoElement && this.videoElement.parentNode) {
      this.videoElement.pause()
      this.videoElement.srcObject = null
      this.videoElement.parentNode.removeChild(this.videoElement)
    }
    this.videoElement = null

    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(t => t.stop())
    }
    this.cameraStream = null

    if (this.canvasStream) {
      this.canvasStream.getTracks().forEach(t => t.stop())
    }
    this.canvasStream = null

    if (this.compositeStream) {
      this.compositeStream.getTracks().forEach(t => t.stop())
    }
    this.compositeStream = null

    if (this.audioContext) {
      try { this.audioContext.close() } catch (e) {}
      this.audioContext = null
      this.audioDestination = null
    }

    if (this.compositeCanvas && this.compositeCanvas.parentNode) {
      // keep canvas detached (it was never attached), just drop refs
    }
    this.compositeCanvas = null
    this.compositeContext = null
    this.arCanvas = null
  }
}
